import { isEmpty } from "@swiggy-private/js-utils";

export type LottiePlaceholderMap = Record<string, string>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const cloneLottieData = (lottieData: any): any => JSON.parse(JSON.stringify(lottieData));

/**
 * Sets the document text (`t.d.k[].s.t`) on a Lottie text layer.
 * Leaves the layer name (`nm`) unchanged.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const setLayerDocumentText = (layer: any, text: string): void => {
    const keyframes = layer?.t?.d?.k;
    if (!Array.isArray(keyframes)) {
        return;
    }

    keyframes.forEach((keyframe: { s?: { t?: string } }) => {
        if (keyframe?.s != null) {
            keyframe.s.t = text;
        }
    });
};

/**
 * Finds layers whose `nm` matches a placeholder key and updates their text document.
 */
const applyPlaceholdersToLayers = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    layers: any[] | undefined,
    placeholders: LottiePlaceholderMap,
): void => {
    if (!layers?.length) {
        return;
    }

    layers.forEach((layer) => {
        const layerName = layer?.nm;
        if (typeof layerName !== "string" || typeof placeholders[layerName] !== "string") {
            return;
        }
        setLayerDocumentText(layer, placeholders[layerName]);
    });
};

/**
 * Searches Lottie layers (and precomp asset layers) by `nm` for placeholder keys,
 * then updates each matching text layer's document text with the mapped value.
 *
 * @param lottieData - The Lottie animation JSON data.
 * @param placeholders - Map where each key is a layer `nm` to match and
 * the value is the replacement text for that layer's text document.
 * @returns A new copy of the Lottie data with text updated. The original data is
 * left untouched. If the data or placeholders are empty, the original data is returned as-is.
 * Returns undefined if replacement fails unexpectedly.
 */
export const replaceLottiePlaceholders = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    lottieData: any,
    placeholders: LottiePlaceholderMap,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
    if (isEmpty(lottieData) || isEmpty(placeholders)) {
        return lottieData;
    }

    try {
        const updatedLottieData = cloneLottieData(lottieData);

        applyPlaceholdersToLayers(updatedLottieData.layers, placeholders);

        if (Array.isArray(updatedLottieData.assets)) {
            updatedLottieData.assets.forEach((asset: { layers?: unknown[] }) => {
                applyPlaceholdersToLayers(asset?.layers, placeholders);
            });
        }

        return updatedLottieData;
    } catch {
        return undefined;
    }
};
